import type {
  OntologyBaseType,
  OntologyExplorerDoc,
  OntologyNodeType,
  OntologyRelationshipType,
  OntologyUsage,
} from '#/api/ontology'

// Application-populated identity/audit/tenancy properties — present on every
// type, never written by the LLM, and noise when reading the ontology. The
// property tables hide them behind a toggle (mirrors the platform's
// GLOBAL_SKIP set).
export const INTERNAL_PROPERTY_NAMES: ReadonlySet<string> = new Set([
  'uuid',
  'created_at',
  'updated_at',
  'app_org_id',
  'enterprise_id',
])

// The artefact carries every layer; the UI shows only the layer keys the
// artefact itself enables (explorer_layer_keys). Hidden layers are absent
// entirely — no greyed-out chrome.
export function visibleNodeTypes(
  doc: OntologyExplorerDoc,
): Array<OntologyNodeType> {
  const keys = new Set(doc.explorer_layer_keys)
  return doc.node_types.filter((nt) => keys.has(nt.layer_key))
}

// Usages whose two endpoints are both visible node types. A relationship
// declared in a visible layer can still name a hidden endpoint (usages are
// merged across layers platform-side), so endpoint visibility is the filter
// that matters — not the relationship's own layer_key alone.
export function visibleUsages(
  rel: OntologyRelationshipType,
  visibleNames: ReadonlySet<string>,
): Array<OntologyUsage> {
  return rel.usages.filter(
    (u) => visibleNames.has(u.from) && visibleNames.has(u.to),
  )
}

// Relationships with at least one fully-visible usage, in artefact order.
export function visibleRelationshipTypes(
  doc: OntologyExplorerDoc,
): Array<OntologyRelationshipType> {
  const names = new Set(visibleNodeTypes(doc).map((nt) => nt.name))
  return doc.relationship_types.filter(
    (rel) => visibleUsages(rel, names).length > 0,
  )
}

// The relationships a type participates in, split by its role. A rel appears
// under `out` when the type is a usage's `from`, under `in` when it is a `to`
// (both, for self-referential rels like Entity HAS_COMPONENT Entity).
export function relationshipsFor(
  doc: OntologyExplorerDoc,
  typeName: string,
): {
  out: Array<OntologyRelationshipType>
  in: Array<OntologyRelationshipType>
} {
  const names = new Set(visibleNodeTypes(doc).map((nt) => nt.name))
  const out: Array<OntologyRelationshipType> = []
  const incoming: Array<OntologyRelationshipType> = []
  for (const rel of visibleRelationshipTypes(doc)) {
    const usages = visibleUsages(rel, names)
    if (usages.some((u) => u.from === typeName)) out.push(rel)
    if (usages.some((u) => u.to === typeName)) incoming.push(rel)
  }
  return { out, in: incoming }
}

// The abstract base types a concrete type inherits from, nearest ancestor
// first. These carry the tenancy and audit shape every node shares, and the
// `extends` badge / `from X` property chips reference them by name — so
// without this the reader has no way to look up what they mean.
export function inheritanceChain(
  doc: OntologyExplorerDoc,
  extendsName: string | null | undefined,
): Array<OntologyBaseType> {
  const byName = new Map(doc.base_types.map((bt) => [bt.name, bt]))
  const chain: Array<OntologyBaseType> = []
  let next = extendsName
  // Guard against a cycle in hand-written YAML rather than hanging the page.
  while (next != null && !chain.some((bt) => bt.name === next)) {
    const base = byName.get(next)
    if (!base) break
    chain.push(base)
    next = base.extends
  }
  return chain
}

// Stable colour per visible type: position in the visible list cycled over
// the six chart tokens. Display-only — never key data on the colour.
export function typeColorVar(
  doc: OntologyExplorerDoc,
  typeName: string,
): string {
  const index = visibleNodeTypes(doc).findIndex((nt) => nt.name === typeName)
  const bucket = index >= 0 ? (index % 6) + 1 : 1
  return `var(--chart-${bucket})`
}
